import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiLayers,
  FiPhoneCall,
  FiTrendingUp,
} from "react-icons/fi";
import { useLocalContext } from "../../context/LocalContext";
import "./Hero.css";

const Hero = ({ onSubmitLead }) => {
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo?.name || "Your Brand";
  const phoneDisplay = webinfo?.phone || webinfo?.phonecall || "+1 (000) 000-0000";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
  });

  const [status, setStatus] = useState({
    loading: false,
    ok: "",
    err: "",
  });

  const services = [
    "Website Design & Development",
    "Landing Page Optimization",
    "SEO & Organic Growth",
    "Brand Website Revamp",
    "Automation & CRM Setup",
  ];

  const trustPoints = [
    "Strategy-led design, not just aesthetics",
    "Fast, mobile-optimized, fully responsive",
    "Ongoing support & clear communication",
  ];

  const metrics = [
    { value: "35+", label: "Websites Delivered", icon: <FiLayers /> },
    { value: "4.9★", label: "Client Satisfaction", icon: <FiTrendingUp /> },
    { value: "<24h", label: "Response Time", icon: <FiClock /> },
  ];

  const onChange = (e) => {
    setStatus({ loading: false, ok: "", err: "" });
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.phone.trim()) {
      setStatus({
        loading: false,
        ok: "",
        err: "Please enter your name and phone number.",
      });
      return;
    }

    try {
      setStatus({ loading: true, ok: "", err: "" });

      await onSubmitLead?.({
        ...form,
        source: "premium_modern_hero",
      });

      setStatus({
        loading: false,
        ok: "Thanks! Our team will reach out shortly.",
        err: "",
      });

      setForm({
        name: "",
        phone: "",
        service: "",
      });
    } catch (error) {
      setStatus({
        loading: false,
        ok: "",
        err: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <section className="heroPremium">
      <div className="heroPremium-blur heroPremium-blurOne" />
      <div className="heroPremium-blur heroPremium-blurTwo" />

      <div className="heroPremium-wrap">
        <motion.div
          className="heroPremium-left"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <div className="heroPremium-badge">
            <span className="heroPremium-badgeDot" />
            Trusted by ambitious businesses
          </div>

          <h1 className="heroPremium-title">
            Websites that work
            <span>as hard as you do</span>
          </h1>

          <p className="heroPremium-subtitle">
            {brandName} crafts clean, high-performing websites and digital
            strategies that attract the right audience and convert them
            consistently — with no fluff, just results.
          </p>

          <div className="heroPremium-points">
            {trustPoints.map((item) => (
              <div className="heroPremium-point" key={item}>
                <FiCheckCircle />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="heroPremium-actions">
            <a href="/services" className="heroPremium-btn heroPremium-btnPrimary">
              Explore Services
              <FiArrowRight />
            </a>

            <a href={`tel:${phoneDisplay}`} className="heroPremium-btn heroPremium-btnGhost">
              <FiPhoneCall />
              Call Now
            </a>
          </div>

          <div className="heroPremium-stats">
            {metrics.map((item) => (
              <div className="heroPremium-statCard" key={item.label}>
                <div className="heroPremium-statIcon">{item.icon}</div>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="heroPremium-right"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.12 }}
        >
          <div className="heroPremium-formCard">
            <div className="heroPremium-formTop">
              <p className="heroPremium-formEyebrow">Free strategy call</p>
              <h2>Let's plan your project</h2>
              <p>
                Tell us what you need and we'll outline a clear, focused plan
                for your business — no pressure, no obligation.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="heroPremium-form">
              <div className="heroPremium-field">
                <label htmlFor="name">Your Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="heroPremium-field">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="heroPremium-field">
                <label htmlFor="service">What do you need?</label>
                <select
                  id="service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              {status.err ? <div className="heroPremium-msg heroPremium-msgErr">{status.err}</div> : null}
              {status.ok ? <div className="heroPremium-msg heroPremium-msgOk">{status.ok}</div> : null}

              <button
                type="submit"
                className="heroPremium-submit"
                disabled={status.loading}
              >
                {status.loading ? "Submitting..." : "Book a Free Strategy Call"}
              </button>
            </form>

            <div className="heroPremium-miniTrust">
              <span>No commitment</span>
              <span>Free guidance</span>
              <span>Fast reply</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
